import { DatePipe, NgClass, NgFor, NgIf } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatNativeDateModule } from "@angular/material/core";
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { Router } from "@angular/router";
import { firstValueFrom } from "rxjs";
import { CapturaManualSemanal } from "src/app/shared/model/tarea/captura-manual-semanal.model";
import { TareaDiaria } from "src/app/shared/model/tarea/tareaDiaria.model";
import { TiempoTarea } from "src/app/shared/model/tarea/tiempoTarea.model";
import { GenericoService } from "src/app/shared/services/generico.service";
import { SweetAlertComponent } from "src/app/shared/SweetMesssage";
import { UrlConstantes } from "src/app/utilerias/constantes/urlconstantes";

@Component({
  selector: "app-captura-manual-semanal",
  standalone: true,
  templateUrl: "./captura-manual-semanal.component.html",
  styleUrls: ["./captura-manual-semanal.component.css"],
  imports: [
    NgIf,
    NgFor,
    NgClass,
    FormsModule,
    DatePipe,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
})
export class CapturaManualSemanalComponent implements OnInit {
  semana = new CapturaManualSemanal();
  filtro = new TareaDiaria();
  registro = new TareaDiaria();
  fechaBloque = new Date();
  horaInicio = "";
  horaFin = "";
  guardando = false;

  constructor(
    private readonly generico: GenericoService,
    private readonly sweetAlert: SweetAlertComponent,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    const hoy = new Date();
    const dia = hoy.getDay() || 7;
    const lunes = new Date(hoy);
    lunes.setDate(hoy.getDate() - dia + 1);
    const domingo = new Date(lunes);
    domingo.setDate(lunes.getDate() + 6);
    this.filtro.fecha_inicio = lunes;
    this.filtro.fecha_fin = domingo;
    this.fechaBloque = lunes;
    void this.cargarSemana();
  }

  async cargarSemana(): Promise<void> {
    try {
      const response = (await firstValueFrom(
        this.generico.obtenerElementoPorCampoPost(
          UrlConstantes.CONSULTA_SEMANA_MANUAL,
          this.filtro
        )
      )) as CapturaManualSemanal;
      this.semana = response ?? new CapturaManualSemanal();
    } catch {
      this.semana = new CapturaManualSemanal();
      this.sweetAlert.showSwal("error-message", null);
    }
  }

  async registrarBloque(): Promise<void> {
    if (!this.registro.id || !this.fechaBloque || !this.horaInicio || !this.horaFin || !this.registro.motivoRegistro) {
      return;
    }

    this.guardando = true;
    const payload = new TareaDiaria();
    payload.id = this.registro.id;
    payload.motivoRegistro = this.registro.motivoRegistro;
    payload.fecha_inicio_str = this.componerFechaHora(this.fechaBloque, this.horaInicio);
    payload.fecha_fin_str = this.componerFechaHora(this.fechaBloque, this.horaFin);

    try {
      const response = (await firstValueFrom(
        this.generico.obtenerElementoPorCampoPost(
          UrlConstantes.REGISTRAR_BLOQUE_MANUAL,
          payload
        )
      )) as TareaDiaria;

      if (response?.pCodRetorno === "00000") {
        this.sweetAlert.showSwal("success-message", response.pMensaje);
        this.registro = new TareaDiaria();
        this.horaInicio = "";
        this.horaFin = "";
        await this.cargarSemana();
      } else {
        this.sweetAlert.showSwal("error-message-msg", response?.pMensaje || null);
      }
    } catch {
      this.sweetAlert.showSwal("error-message", null);
    } finally {
      this.guardando = false;
    }
  }

  volverAJornada(): void {
    this.router.navigate(["/TAREA/tareaConsulta"]);
  }

  get bloquesOrdenados(): TiempoTarea[] {
    return [...(this.semana?.bloques ?? [])].sort((a, b) => {
      return new Date(b.inicio).getTime() - new Date(a.inicio).getTime();
    });
  }

  resolverNombreTarea(idTarea: number): string {
    const tarea = (this.semana?.tareas ?? []).find((item) => item.id === idTarea);
    return tarea?.nombre || `Tarea ${idTarea}`;
  }

  estatusBloque(bloque: TiempoTarea): string {
    if (!bloque?.idAutorizacion) {
      return "VALIDADO";
    }
    return bloque.estatusAutorizacion || "PENDIENTE";
  }

  resumenBloque(bloque: TiempoTarea): string {
    const partes: string[] = [];
    partes.push(bloque.origenRegistro || "TIMER");
    if (bloque.cierreAutomatico) {
      partes.push("Cierre automático");
    }
    if (bloque.tiempoPendienteStr && bloque.tiempoPendienteStr !== "00:00:00") {
      partes.push(`Pendiente ${bloque.tiempoPendienteStr}`);
    }
    if (bloque.tiempoRechazadoStr && bloque.tiempoRechazadoStr !== "00:00:00") {
      partes.push(`Rechazado ${bloque.tiempoRechazadoStr}`);
    }
    return partes.join(" · ");
  }

  private componerFechaHora(fecha: Date, hora: string): string {
    const [hh, mm] = hora.split(":");
    const base = new Date(fecha);
    base.setHours(Number(hh), Number(mm), 0, 0);
    const normalizada = new Date(base.getTime() - base.getTimezoneOffset() * 60000);
    return normalizada.toISOString().slice(0, 19).replace("T", " ");
  }
}
